import { Controller } from "@hotwired/stimulus"

// The admin question form: shows the options list only for answer types that
// have options, explains the chosen type, and adds or removes option rows.
// Rows are cloned from a <template>; each gets a unique index so Rails
// nested attributes don't collide.
export default class extends Controller {
  static targets = ["typeSelect", "typeHint", "optionsCard", "container", "template", "bulk", "bulkDetails"]
  static values = { optionTypes: Array }

  connect() {
    this.counter = 0
    this.typeChanged()
  }

  typeChanged() {
    const chosen = this.typeSelectTarget.selectedOptions[0]
    const hasOptions = this.optionTypesValue.includes(this.typeSelectTarget.value)

    this.optionsCardTarget.hidden = !hasOptions
    if (this.hasTypeHintTarget) this.typeHintTarget.textContent = chosen?.dataset.hint || ""
  }

  add(event) {
    event?.preventDefault()
    this.insertRow("").querySelector("input[name*='[label]']").focus()
  }

  // One option per line, pasted from a spreadsheet or typed in one go.
  addMany(event) {
    event.preventDefault()
    const labels = this.bulkTarget.value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    labels.forEach((label) => this.insertRow(label))
    this.bulkTarget.value = ""
    if (this.hasBulkDetailsTarget) this.bulkDetailsTarget.open = false
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-sortable-target='item']")
    const destroy = row.querySelector("input[name*='[_destroy]']")
    const id = row.querySelector("input[name*='[id]']")

    if (destroy && id) {
      destroy.value = "1"
      row.hidden = true
    } else {
      row.remove()
    }
    this.rowsChanged()
  }

  // Enter in an option label moves on to the next option instead of submitting.
  labelKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()

    const row = event.target.closest("[data-sortable-target='item']")
    const next = this.nextVisibleRow(row)
    if (next) {
      next.querySelector("input[name*='[label]']").focus()
    } else {
      this.add()
    }
  }

  insertRow(label) {
    // Strong parameters only read a nested hash as a list of rows when every
    // key is numeric, so the index has to be digits — not "new_…".
    const index = `${Date.now()}${this.counter++}`
    this.containerTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML.replaceAll("NEW_RECORD", index))
    const row = this.containerTarget.lastElementChild
    row.querySelector("input[name*='[label]']").value = label
    this.rowsChanged()
    return row
  }

  // Tells the sortable list around the rows to renumber them. Dispatched from
  // the container so it bubbles up to that list, not just to the form.
  rowsChanged() {
    this.dispatch("changed", { target: this.containerTarget })
  }

  nextVisibleRow(row) {
    let next = row.nextElementSibling
    while (next && next.hidden) next = next.nextElementSibling
    return next
  }
}
